import React, { useState, useEffect } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { Line } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import { LinearMortgageFormData, LinearMortgageResult, EnergyLabel } from '../types';
import {
  calculateLinearMortgage,
  validateLinearMortgageForm,
  formatCurrency,
  parseNumber,
  formatInputNumber,
  calculateEigenwoningforfait,
  calculateNHGFee
} from '../utils/mortgageCalculations';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const LinearCalculator: React.FC = () => {
  const [formData, setFormData] = useState<LinearMortgageFormData>({
    age: 35,
    income: 60000,
    partnerIncome: 0,
    liabilities: 300,
    term: 20,
    rate: 4.0,
    energyLabel: 'C' as EnergyLabel,
    buyerCosts: 2.0,
    useNHG: false,
    nhgLimit: 435000,
    nhgDiscount: 0.20,
    nhgFeeRate: 0.60,
    mortgageAmount: 300000,
    mortgageDeductionRate: 37.48,
    houseValue: 400000,
    useMaxMortgage: false
  });

  const [result, setResult] = useState<LinearMortgageResult | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [nhgFee, setNhgFee] = useState(0);

  const energyLabels: EnergyLabel[] = ['G', 'F', 'E', 'D', 'C', 'B', 'A', 'A+', 'A++', 'A+++', 'A++++', 'A++++ garantie'];

  useEffect(() => {
    if (formData.useNHG && result) {
      setNhgFee(calculateNHGFee(result.mortgageAmount, formData.nhgFeeRate));
    } else {
      setNhgFee(0);
    }
  }, [formData.useNHG, formData.nhgFeeRate, result]);

  const handleInputChange = (field: keyof LinearMortgageFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNumberInputChange = (field: keyof LinearMortgageFormData, value: string) => {
    if (field === 'income' || field === 'partnerIncome' || field === 'liabilities' || field === 'mortgageAmount' || field === 'houseValue') {
      const numValue = parseNumber(value);
      setFormData(prev => ({ ...prev, [field]: numValue }));
    } else {
      const numValue = parseFloat(value) || 0;
      setFormData(prev => ({ ...prev, [field]: numValue }));
    }
  };

  const handleFormattedInputChange = (field: 'income' | 'partnerIncome' | 'liabilities' | 'mortgageAmount' | 'houseValue', event: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatInputNumber(event.target.value);
    event.target.value = formatted;
    handleNumberInputChange(field, formatted);
  };

  const handleCalculate = () => {
    const validationErrors = validateLinearMortgageForm(formData);

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      setShowResults(false);
      return;
    }

    setErrors([]);
    const calculationResult = calculateLinearMortgage(formData);
    setResult(calculationResult);
    setShowResults(true);

    // Scroll to results
    setTimeout(() => {
      const resultsElement = document.getElementById('results-section');
      if (resultsElement) {
        resultsElement.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const getChartData = () => {
    if (!result) return null;

    // Take every 12th month for yearly view, plus the first and last month
    const yearlyPayments = result.monthlyPayments.filter((_, index) =>
      index === 0 || index === result.monthlyPayments.length - 1 || (index + 1) % 12 === 0
    );

    const labels = yearlyPayments.map(payment => {
      if (payment.month === 1) return 'Maand 1';
      if (payment.month === result.monthlyPayments.length) return `Maand ${payment.month}`;
      return `Jaar ${Math.ceil(payment.month / 12)}`;
    });

    // Monthly payment development
    const paymentData = {
      labels,
      datasets: [
        {
          label: 'Totale Maandlast',
          data: yearlyPayments.map(payment => payment.totalPayment),
          borderColor: 'rgb(0, 115, 230)',
          backgroundColor: 'rgba(0, 115, 230, 0.1)',
          fill: false,
        },
        {
          label: 'Rente',
          data: yearlyPayments.map(payment => payment.interest),
          borderColor: 'rgb(220, 53, 69)',
          backgroundColor: 'rgba(220, 53, 69, 0.1)',
          fill: false,
        },
        {
          label: 'Aflossing',
          data: yearlyPayments.map(payment => payment.principal),
          borderColor: 'rgb(0, 51, 102)',
          backgroundColor: 'rgba(0, 51, 102, 0.1)',
          fill: false,
        },
        {
          label: 'Netto totaal (na aftrek)',
          data: yearlyPayments.map(payment => payment.netPayment),
          borderColor: 'rgb(40, 167, 69)',
          backgroundColor: 'rgba(40, 167, 69, 0.1)',
          fill: false,
        }
      ]
    };

    // Balance over time
    const balanceData = {
      labels,
      datasets: [
        {
          label: 'Restschuld',
          data: yearlyPayments.map(payment => payment.balance),
          borderColor: 'rgb(40, 167, 69)',
          backgroundColor: 'rgba(40, 167, 69, 0.1)',
          fill: true,
        }
      ]
    };

    return { paymentData, balanceData };
  };

  const chartData = getChartData();

  return (
    <div className="fade-in">
      <div className="card">
        <h1>Lineaire Hypotheek Calculator</h1>
        <p>
          Bereken uw maandlasten voor een lineaire hypotheek. Bij deze hypotheekvorm
          lost u elke maand hetzelfde bedrag af, waardoor uw maandlasten afnemen over de tijd.
        </p>
        <div className="alert alert-warning">
          <strong>Let op:</strong> Deze berekening is louter indicatief. Voor een nauwkeurige berekening en het verkennen van andere opties, <Link to="/contact">neem contact op met De Heer</Link>. Voor algemene budgetteringsvraagstukken kunt u ook terecht bij <a href="https://www.nibud.nl/" target="_blank" rel="noopener noreferrer">NIBUD</a>.
        </div>
      </div>

      {errors.length > 0 && (
        <div className="alert alert-error">
          <strong>Let op:</strong>
          <ul style={{ marginTop: '0.5rem', marginBottom: 0 }}>
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="card">
        <h2>Calculator Instellingen</h2>

        <div className="grid grid-2">
          <div className="form-group">
            <label htmlFor="age">
              Leeftijd
              <input
                type="number"
                id="age"
                min="18"
                max="75"
                value={formData.age}
                onChange={(e) => handleInputChange('age', parseInt(e.target.value) || 0)}
              />
            </label>
          </div>

          <div className="form-group">
            <label htmlFor="income">
              Bruto jaarinkomen (€)
              <input
                type="text"
                id="income"
                placeholder="60.000"
                value={formatInputNumber(formData.income.toString())}
                onChange={(e) => handleFormattedInputChange('income', e)}
              />
            </label>
          </div>

          <div className="form-group">
            <label htmlFor="partnerIncome">
              Inkomen van partner (€) - optioneel
              <input
                type="text"
                id="partnerIncome"
                placeholder="0"
                value={formatInputNumber(formData.partnerIncome.toString())}
                onChange={(e) => handleFormattedInputChange('partnerIncome', e)}
              />
            </label>
          </div>

          <div className="form-group">
            <label htmlFor="liabilities">
              Bestaande verplichtingen (€ p/m)
              <input
                type="text"
                id="liabilities"
                placeholder="300"
                value={formatInputNumber(formData.liabilities.toString())}
                onChange={(e) => handleFormattedInputChange('liabilities', e)}
              />
            </label>
          </div>



          <div className="form-group">
            <label htmlFor="term">
              Looptijd (jaar)
              <select
                id="term"
                value={formData.term}
                onChange={(e) => handleInputChange('term', parseInt(e.target.value))}
              >
                <option value={10}>10</option>
                <option value={15}>15</option>
                <option value={20}>20</option>
                <option value={25}>25</option>
                <option value={30}>30</option>
              </select>
            </label>
          </div>

          <div className="form-group">
            <label htmlFor="rate">
              Basisrente (%)
              <input
                type="number"
                id="rate"
                step="0.01"
                min="0"
                max="20"
                value={formData.rate}
                onChange={(e) => handleInputChange('rate', parseFloat(e.target.value) || 0)}
              />
            </label>
          </div>

          <div className="form-group">
            <label htmlFor="houseValue">
              Huiswaarde (€)
              <input
                type="text"
                id="houseValue"
                placeholder="400.000"
                value={formatInputNumber(formData.houseValue.toString())}
                onChange={(e) => handleFormattedInputChange('houseValue', e)}
              />
            </label>
          </div>

          <div className="form-group">
            <label htmlFor="energyLabel">
              Energielabel
              <select
                id="energyLabel"
                value={formData.energyLabel}
                onChange={(e) => handleInputChange('energyLabel', e.target.value as EnergyLabel)}
              >
                {energyLabels.map(label => (
                  <option key={label} value={label}>{label}</option>
                ))}
              </select>
            </label>
          </div>
        </div>

        <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={formData.useMaxMortgage}
              onChange={(e) => handleInputChange('useMaxMortgage', e.target.checked)}
            />
            Maximale hypotheek berekenen
          </label>
        </div>

        {formData.useMaxMortgage && (
          <div className="alert alert-info" style={{ marginTop: '0.5rem' }}>
            De maximale hypotheek wordt berekend op basis van uw inkomen, leeftijd en andere factoren.
            <br /><strong>Let op:</strong> Bij een lineaire hypotheek is de maximale hypotheek vaak lager dan bij een annuïteitenhypotheek, omdat de maandlasten aan het begin hoger zijn.
          </div>
        )}

        {!formData.useMaxMortgage && (
          <div className="form-group">
            <label htmlFor="mortgageAmount">
              Hypotheekbedrag (€)
              <input
                type="text"
                id="mortgageAmount"
                placeholder="300.000"
                value={formatInputNumber(formData.mortgageAmount.toString())}
                onChange={(e) => handleFormattedInputChange('mortgageAmount', e)}
              />
            </label>
          </div>
        )}

        <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={formData.useNHG}
              onChange={(e) => handleInputChange('useNHG', e.target.checked)}
            />
            Vraag NHG aan
          </label>
        </div>

        {formData.useNHG && (
          <fieldset>
            <legend>NHG-instellingen</legend>

            <div className="grid grid-3">
              <div className="form-group">
                <label htmlFor="nhgLimit">
                  NHG-grens (€)
                  <input
                    type="number"
                    id="nhgLimit"
                    step="1000"
                    value={formData.nhgLimit}
                    onChange={(e) => handleInputChange('nhgLimit', parseInt(e.target.value) || 0)}
                  />
                </label>
              </div>

              <div className="form-group">
                <label htmlFor="nhgDiscount">
                  Rentevoordeel NHG (%)
                  <input
                    type="number"
                    id="nhgDiscount"
                    step="0.01"
                    min="0"
                    value={formData.nhgDiscount}
                    onChange={(e) => handleInputChange('nhgDiscount', parseFloat(e.target.value) || 0)}
                  />
                </label>
              </div>

              <div className="form-group">
                <label htmlFor="nhgFeeRate">
                  NHG-premie (%)
                  <input
                    type="number"
                    id="nhgFeeRate"
                    step="0.01"
                    min="0"
                    value={formData.nhgFeeRate}
                    onChange={(e) => handleInputChange('nhgFeeRate', parseFloat(e.target.value) || 0)}
                  />
                </label>
              </div>
            </div>

            {nhgFee > 0 && (
              <div className="alert alert-warning">
                <strong>Eenmalige NHG-premie:</strong> {formatCurrency(nhgFee)}
              </div>
            )}
          </fieldset>
        )}

        <button
          className="btn btn-primary"
          onClick={handleCalculate}
        >
          Bereken Lineaire Hypotheek
        </button>
      </div>

      {showResults && result && (
        <div id="results-section" className="fade-in">
          <div className="alert alert-info">
            <strong>Disclaimer:</strong> Deze berekening is louter indicatief. Voor een nauwkeurige berekening en het verkennen van andere opties, <Link to="/contact">neem contact op met DeHeer</Link>. Voor algemene budgettering kunt u ook terecht bij <a href="https://www.nibud.nl/" target="_blank" rel="noopener noreferrer">NIBUD</a>.
          </div>

          <div className="card">
            <h2>Resultaten</h2>

            <div className="summary-info" style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
              <div className="grid grid-3">
                <div>
                  <h4 title="De Wet Hillen wordt niet meegenomen in deze berekening. De Wet Hillen leidt tot een aftrek op de EWF voor een lager resterend rentebedrag; als de EWF hoger is dan de aftrekbare kosten.">
                    Eigenwoningforfait (EWF) ℹ️
                  </h4>
                  <p><strong>Jaarlijks:</strong> {formatCurrency(calculateEigenwoningforfait(formData.houseValue))}</p>
                  <p><strong>Maandelijks:</strong> {formatCurrency(calculateEigenwoningforfait(formData.houseValue) / 12)}</p>
                  <p style={{ fontSize: '0.9em', color: '#666' }}>
                    Dit bedrag wordt toegevoegd aan uw belastbaar inkomen
                  </p>
                </div>
                <div>
                  <h4>Totaal bruto inkomen</h4>
                  <p><strong>{formatCurrency(formData.income + formData.partnerIncome)}</strong></p>
                  <p style={{ fontSize: '0.9em', color: '#666' }}>
                    Uw inkomen: {formatCurrency(formData.income)}
                    {formData.partnerIncome > 0 && (
                      <><br />Partner: {formatCurrency(formData.partnerIncome)}</>
                    )}
                  </p>
                </div>
                <div>
                  <h4>Huiswaarde</h4>
                  <p><strong>{formatCurrency(formData.houseValue)}</strong></p>
                </div>
              </div>
            </div>

            <div className="grid grid-2 mb-4">
              <div>
                <h3>Samenvatting</h3>
                <table style={{ width: '100%' }}>
                  <tbody>
                    <tr>
                      <td><strong>Hypotheekbedrag:</strong></td>
                      <td className="text-right">{formatCurrency(result.mortgageAmount)}</td>
                    </tr>
                    {formData.useMaxMortgage && (
                      <tr>
                        <td><strong>Maximale hypotheek:</strong></td>
                        <td className="text-right text-success">{formatCurrency(result.mortgageAmount)}</td>
                      </tr>
                    )}
                    <tr>
                      <td><strong>Kosten koper:</strong></td>
                      <td className="text-right">{formatCurrency(result.mortgageAmount * (formData.buyerCosts / 100))}</td>
                    </tr>
                    <tr>
                      <td><strong>Totale rente:</strong></td>
                      <td className="text-right">{formatCurrency(result.totalInterest)}</td>
                    </tr>
                    <tr>
                      <td><strong>Totaal betaald:</strong></td>
                      <td className="text-right">{formatCurrency(result.totalPayment)}</td>
                    </tr>
                    <tr>
                      <td><strong>Gemiddelde maandlast:</strong></td>
                      <td className="text-right">{formatCurrency(result.averageMonthlyPayment)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div>
                <h3>Eerste en Laatste Maand</h3>
                <table style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th>Maand</th>
                      <th>Maandlast</th>
                      <th>Netto maandlast</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Maand 1</td>
                      <td className="text-right">{formatCurrency(result.monthlyPayments[0].totalPayment)}</td>
                      <td className="text-right font-bold">{formatCurrency(result.monthlyPayments[0].netPayment)}</td>
                    </tr>
                    <tr>
                      <td>Laatste maand</td>
                      <td className="text-right">{formatCurrency(result.monthlyPayments[result.monthlyPayments.length - 1].totalPayment)}</td>
                      <td className="text-right font-bold">{formatCurrency(result.monthlyPayments[result.monthlyPayments.length - 1].netPayment)}</td>
                    </tr>
                    <tr>
                      <td><strong>Verschil</strong></td>
                      <td className="text-right">
                        <strong>
                          {formatCurrency(result.monthlyPayments[0].totalPayment - result.monthlyPayments[result.monthlyPayments.length - 1].totalPayment)}
                        </strong>
                      </td>
                      <td className="text-right">
                        <strong>
                          {formatCurrency(result.monthlyPayments[0].netPayment - result.monthlyPayments[result.monthlyPayments.length - 1].netPayment)}
                        </strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="table-container">
              <h3>Jaarlijks Overzicht</h3>
              <table>
                <thead>
                  <tr>
                    <th>Jaar</th>
                    <th>Maandlast Begin</th>
                    <th>Maandlast Eind</th>
                    <th>Rente Begin</th>
                    <th>Rente Eind</th>
                    <th>Aflossing</th>
                    <th>Restschuld</th>
                    <th>Netto maandlast Begin</th>
                    <th>Netto maandlast Eind</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: formData.term }, (_, yearIndex) => {
                    const startMonth = yearIndex * 12;
                    const endMonth = Math.min((yearIndex + 1) * 12 - 1, result.monthlyPayments.length - 1);
                    const startPayment = result.monthlyPayments[startMonth];
                    const endPayment = result.monthlyPayments[endMonth];

                    return (
                      <tr key={yearIndex}>
                        <td>{yearIndex + 1}</td>
                        <td>{formatCurrency(startPayment.totalPayment)}</td>
                        <td>{formatCurrency(endPayment.totalPayment)}</td>
                        <td>{formatCurrency(startPayment.interest)}</td>
                        <td>{formatCurrency(endPayment.interest)}</td>
                        <td>{formatCurrency(startPayment.principal)}</td>
                        <td>{formatCurrency(endPayment.balance)}</td>
                        <td className="font-bold">{formatCurrency(startPayment.netPayment)}</td>
                        <td className="font-bold">{formatCurrency(endPayment.netPayment)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {chartData && (
            <>
              <div className="chart-container">
                <h3>Ontwikkeling Maandlasten</h3>
                <Line
                  data={chartData.paymentData}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: {
                        position: 'top' as const,
                      },
                      title: {
                        display: false,
                      },
                    },
                    scales: {
                      x: {
                        title: {
                          display: true,
                          text: 'Tijd'
                        }
                      },
                      y: {
                        title: {
                          display: true,
                          text: 'Bedrag (€)'
                        }
                      }
                    }
                  }}
                />
              </div>

              <div className="chart-container">
                <h3>Restschuld Ontwikkeling</h3>
                <Line
                  data={chartData.balanceData}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: {
                        position: 'top' as const,
                      },
                      title: {
                        display: false,
                      },
                    },
                    scales: {
                      x: {
                        title: {
                          display: true,
                          text: 'Tijd'
                        }
                      },
                      y: {
                        title: {
                          display: true,
                          text: 'Restschuld (€)'
                        }
                      }
                    }
                  }}
                />
              </div>
            </>
          )}

          <div className="card">
            <h3>Voordelen van een Lineaire Hypotheek</h3>
            <div className="grid grid-2">
              <div>
                <h4>✅ Voordelen</h4>
                <ul>
                  <li>Lagere totale rentekosten</li>
                  <li>Snellere vermogensopbouw</li>
                  <li>Afnemende maandlasten</li>
                  <li>Meer zekerheid over toekomstige lasten</li>
                </ul>
              </div>
              <div>
                <h4>⚠️ Nadelen</h4>
                <ul>
                  <li>Hogere maandlasten aan het begin</li>
                  <li>Minder hypotheekrente aftrekbaar</li>
                  <li>Strengere toetsing door hoge startlasten</li>
                  <li>Minder ruimte voor andere uitgaven</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LinearCalculator;
